import type { MfgSdsBinderCandidate } from "@/lib/manufacturing/msdsBinderTypes";
import {
  ghostBtnClass,
  mfgModalShellClass,
  mfgPrimaryBtnLgClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

type Props = {
  open: boolean;
  loading: boolean;
  exporting: boolean;
  error: string | null;
  plantName: string;
  organizationName: string;
  includesDrafts: boolean;
  candidates: MfgSdsBinderCandidate[];
  onDownload: () => void;
  onClose: () => void;
};

/** MSDS-6 — plant SDS book preview + download. */
export function ManufacturingSdsBookPanel({
  open,
  loading,
  exporting,
  error,
  plantName,
  organizationName,
  includesDrafts,
  candidates,
  onDownload,
  onClose,
}: Props) {
  if (!open) return null;
  const busy = loading || exporting;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4">
      <div
        className={`flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden sm:rounded-2xl ${mfgModalShellClass}`}
      >
        <div className="flex items-start justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-zinc-700">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">SDS book</h3>
            <p className="text-xs text-gray-600 dark:text-zinc-400">
              {organizationName || "Organization"}
              {plantName ? ` · ${plantName}` : ""}
            </p>
          </div>
          <button type="button" className={ghostBtnClass} onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        <div className="space-y-3 overflow-y-auto px-4 py-3">
          {includesDrafts ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
              Includes draft sheets (publish lands in MSDS-5). Each page keeps its own State A or
              State B footer.
            </div>
          ) : null}
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
              {error}
            </div>
          ) : null}
          {loading ? (
            <p className="text-sm text-gray-600 dark:text-zinc-300">Loading binder preview…</p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-zinc-300">
              No required materials with a published SDS at this plant yet. Publish an SDS from
              the material SDS workspace first.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
              {candidates.map((c) => (
                <li
                  key={c.material_id}
                  className="flex items-baseline justify-between gap-2 py-2 text-xs"
                >
                  <div>
                    <div className="font-medium text-gray-900 dark:text-zinc-100">
                      {c.material_code} — {c.material_name}
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-zinc-400">
                      {c.category}
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-900 dark:bg-sky-900/40 dark:text-sky-100">
                    {c.status_chip}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3 dark:border-zinc-700">
          <button
            type="button"
            className={mfgPrimaryBtnLgClass}
            onClick={onDownload}
            disabled={busy || candidates.length === 0}
          >
            {exporting ? "Building PDF…" : "Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
